import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { Download, Calendar, RotateCcw, SlidersHorizontal,X, FileSpreadsheet, Image } from 'lucide-react';
import CustomDatePicker from '../../components/dashboard/CustomDatePicker';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { doctorPatientCount, downloadDoctorPatientCountExcel } from '../../redux/reportsSlice';
import CommonLoader from '../../components/CommonLoader';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import * as htmlToImage from 'html-to-image';
import { getCountries } from '../../redux/masterSlice';
import { FilterPanel, FilterSection, FilterOptionList, FilterCountryList, FilterPopover } from '../../components/common/FilterPanel';
import MasterLabel from '../../components/common/MasterLabel';

// `only` — 'doctors' | 'patients' turns this into the single-series Doctor
// Count / Patient Count page: the other series is not rendered at all (no bar,
// no legend entry, no visibility toggle) and every request carries `only` so
// the API skips the other query. Without the prop it is the original combined
// Doctor & Patient chart, unchanged.
const ReportsCount = ({ only } = {}) => {
    const { t } = useTranslation();
    const doctorsOnly = only === 'doctors';
    const patientsOnly = only === 'patients';
    const singleSeries = doctorsOnly || patientsOnly;
    const exportBase = doctorsOnly ? 'doctor-count' : patientsOnly ? 'patient-count' : 'doctor-patient-count';
    const pageTitle = doctorsOnly
        ? t('reportsCount.doctor_title', 'Doctor Count')
        : patientsOnly
            ? t('reportsCount.patient_title', 'Patient Count')
            : t('reportsCount.title');
    const pageSubtitle = doctorsOnly
        ? t('reportsCount.doctor_subtitle', 'New doctors registered per day')
        : patientsOnly
            ? t('reportsCount.patient_subtitle', 'New patients registered per day')
            : '';
    const dispatch = useDispatch();
    const { doctorPatientCountData, countDataLoading, downloadLoading } = useSelector((state) => state.reports);
    const { countries } = useSelector((state) => state.master);

    const [activeTab, setActiveTab] = useState('month');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [dateErrors, setDateErrors] = useState({ startDate: '', endDate: '' });
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [tempFilters, setTempFilters] = useState({ gender: [], countryId: [] });
    const [filters, setFilters] = useState({ gender: [], countryId: [] });
    const [visibleBars, setVisibleBars] = useState({ doctor: true, patient: true });
    const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState(false);
    // The store slot is shared by both variants (and by earlier visits), so a
    // fresh mount must not paint whatever rows are already there — the other
    // page's rows carry 0 for this series and the total would read 0. Until
    // this mount's own fetch has been dispatched, keep the loader up.
    const [fetchStarted, setFetchStarted] = useState(false);
    const filterButtonRef = useRef(null);
    const filterRef = useRef(null);
    const downloadButtonRef = useRef(null);
    const downloadMenuRef = useRef(null);
    const chartRef = useRef(null);
    const isInitialRender = useRef(true);

    useEffect(() => {
        dispatch(getCountries({ status: 'active', limit: 100 }));
    }, [dispatch]);

    // Helper function to format date to YYYY-MM-DD (local timezone)
    const formatDateToLocal = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Helper function to get date range based on tab
    const getDateRange = (tab) => {
        const today = new Date();
        let from = '';
        let to = '';

        switch (tab) {
            case 'today':
                from = to = formatDateToLocal(today);
                break;
            case 'week':
                // Get current week's Monday to Sunday
                const currentDay = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
                const weekStart = new Date(today);
                weekStart.setDate(today.getDate() - currentDay + (currentDay === 0 ? -6 : 1)); // Go back to Monday
                const weekEnd = new Date(weekStart);
                weekEnd.setDate(weekStart.getDate() + 6); // Add 6 days to get Saturday
                from = formatDateToLocal(weekStart);
                to = formatDateToLocal(weekEnd);
                break;
            case 'month':
                // Get current month's first and last day
                const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
                const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                from = formatDateToLocal(monthStart);
                to = formatDateToLocal(monthEnd);
                break;
            case 'custom':
                from = startDate;
                to = endDate;
                break;
            default:
                from = to = formatDateToLocal(today);
        }

        return { from, to };
    };

    // Sync tempFilters with filters when popup opens
    useEffect(() => {
        if (isFilterOpen) {
            setTempFilters({ ...filters });
        }
    }, [isFilterOpen]);

    // Close filter on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                filterRef.current &&
                !filterRef.current.contains(event.target) &&
                filterButtonRef.current &&
                !filterButtonRef.current.contains(event.target)
            ) {
                // Reset tempFilters to current filters when closing without applying
                setTempFilters({ ...filters });
                setIsFilterOpen(false);
            }

            // Close download menu on click outside
            if (
                downloadMenuRef.current &&
                !downloadMenuRef.current.contains(event.target) &&
                downloadButtonRef.current &&
                !downloadButtonRef.current.contains(event.target)
            ) {
                setIsDownloadMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [filters]);

    // Validate dates
    const validateDates = (start, end) => {
        const errors = { startDate: '', endDate: '' };
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (start) {
            const startDateObj = new Date(start);
            startDateObj.setHours(0, 0, 0, 0);

            // Check if start date is valid
            if (isNaN(startDateObj.getTime())) {
                errors.startDate = t('reportsCount.invalid_date_format');
            }

            // Check if start date is after end date
            if (end) {
                const endDateObj = new Date(end);
                endDateObj.setHours(0, 0, 0, 0);
                if (startDateObj > endDateObj) {
                    errors.startDate = t('reportsCount.start_after_end');
                }
            }
        }

        if (end) {
            const endDateObj = new Date(end);
            endDateObj.setHours(0, 0, 0, 0);

            // Check if end date is valid
            if (isNaN(endDateObj.getTime())) {
                errors.endDate = t('reportsCount.invalid_date_format');
            }

            // Check if end date is in the future
            if (endDateObj > today) {
                errors.endDate = t('reportsCount.end_in_future');
            }

            // Check if end date is before start date
            if (start) {
                const startDateObj = new Date(start);
                startDateObj.setHours(0, 0, 0, 0);
                if (endDateObj < startDateObj) {
                    errors.endDate = t('reportsCount.end_before_start');
                }
            }
        }

        // Check if start date is required when end date is set
        if (end && !start) {
            errors.startDate = t('reportsCount.start_required');
        }

        setDateErrors(errors);
        return Object.values(errors).every(error => !error);
    };

    // Fetch data function
    const fetchCountData = () => {
        const { from, to } = getDateRange(activeTab);

        if (!from || !to) {
            if (activeTab === 'custom') {
                toast.warning(t('reportsCount.select_dates_error'));
            }
            return;
        }

        // For custom, validate dates before fetching
        if (activeTab === 'custom') {
            if (!validateDates(startDate, endDate)) {
                return;
            }
        }

        const params = { from, to };
        if (filters.gender.length > 0) params.gender = filters.gender.join(',');
        if (filters.countryId.length > 0) params.countryId = filters.countryId.join(',');
        if (only) params.only = only;

        console.log('fetchCountData - API Called - From:', from, 'To:', to, 'Genders:', params.gender || 'All', 'Countries:', params.countryId || 'All');
        dispatch(doctorPatientCount(params));
    };

    // Initial load - ONLY runs once on mount
    useEffect(() => {
        console.log('Component mounted - Initial API call');
        fetchCountData();
        setFetchStarted(true);
        // Mark that initial render is complete
        setTimeout(() => {
            isInitialRender.current = false;
        }, 100);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty dependency array - runs ONLY once

    // Handle tab changes (skip initial render)
    useEffect(() => {
        if (!isInitialRender.current) {
            console.log('Tab changed to:', activeTab);
            // Clear dates when switching to custom tab
            if (activeTab === 'custom') {
                setStartDate('');
                setEndDate('');
                setDateErrors({ startDate: '', endDate: '' });
            } else {
                // Fetch data automatically for non-custom tabs
                fetchCountData();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    // Handle custom date changes (skip initial render)
    useEffect(() => {
        if (!isInitialRender.current && activeTab === 'custom' && startDate && endDate) {
            console.log('Custom dates changed - From:', startDate, 'To:', endDate);
            fetchCountData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate]);

    // Handle filter changes (skip initial render)
    useEffect(() => {
        if (!isInitialRender.current) {
            console.log('Filters changed - Gender:', filters.gender, 'Country:', filters.countryId);
            fetchCountData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    // Format data for chart
    const formatChartData = (data) => {
        return data.map(item => ({
            date:
                new Date(item.date).toLocaleDateString(
                    'en-GB',
                    activeTab === 'today' || activeTab === 'week'
                        ? { day: '2-digit', month: '2-digit', year: 'numeric' }
                        : { day: '2-digit' }
                ),
            doctor: item.doctorCount,
            patient: item.patientCount
        }));
    };

    // Show empty data when in custom tab without valid dates
    const shouldShowData = activeTab === 'custom' ? (startDate && endDate) : true;
    const chartData = shouldShowData ? formatChartData(doctorPatientCountData) : [];
    const showLoader = !fetchStarted || countDataLoading;
    // Period total for the single-series pages (the combined chart has none).
    const seriesTotal = doctorsOnly
        ? chartData.reduce((sum, row) => sum + (Number(row.doctor) || 0), 0)
        : patientsOnly
            ? chartData.reduce((sum, row) => sum + (Number(row.patient) || 0), 0)
            : 0;

    // Handle legend click to toggle bar visibility
    const handleLegendClick = (dataKey) => {
        setVisibleBars((prev) => ({
            ...prev,
            [dataKey]: !prev[dataKey]
        }));
    };

    // Handle download Excel
    const handleDownloadExcel = async () => {
        const { from, to } = getDateRange(activeTab);

        if (!from || !to) {
            toast.warning(t('reportsCount.select_dates_error'));
            return;
        }

        // For custom, validate dates before downloading
        if (activeTab === 'custom') {
            if (!validateDates(startDate, endDate)) {
                toast.warning(t('reportsCount.select_dates_error'));
                return;
            }
        }

        setIsDownloadMenuOpen(false);

        try {
            const params = { from, to };
            if (filters.gender.length > 0) params.gender = filters.gender.join(',');
            if (filters.countryId.length > 0) params.countryId = filters.countryId.join(',');
            if (only) params.only = only;

            const resultAction = await dispatch(downloadDoctorPatientCountExcel(params));

            if (downloadDoctorPatientCountExcel.fulfilled.match(resultAction)) {
                const blob = resultAction.payload;
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${exportBase}-${from}-to-${to}.xlsx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                toast.success(t('reportsCount.download_success'));
            } else {
                toast.warning(t('reportsCount.download_error'));
            }
        } catch (error) {
            toast.warning(t('reportsCount.download_error'));
        }
    };

    // Handle download as JPG
    const handleDownloadJPG = async () => {
        const { from, to } = getDateRange(activeTab);

        if (!from || !to) {
            toast.warning(t('reportsCount.select_dates_error'));
            return;
        }

        if (!chartRef.current) {
            toast.warning(t('reportsCount.chart_not_found'));
            return;
        }

        setIsDownloadMenuOpen(false);

        try {
            // Use toJpeg from html-to-image which handles SVG elements better
            const dataUrl = await htmlToImage.toJpeg(chartRef.current, {
                quality: 0.95,
                backgroundColor: '#ffffff',
                pixelRatio: 2,
                cacheBust: true
            });

            const link = document.createElement('a');
            link.href = dataUrl;
            link.download = `${exportBase}-${from}-to-${to}.jpg`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            toast.success(t('reportsCount.chart_download_success'));
        } catch (error) {
            console.error('Error downloading chart:', error);
            toast.warning(t('reportsCount.chart_download_error'));
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-white w-full rounded-2xl p-4 sm:p-6 shadow-md"
            data-testid="reports-count-container"
            data-variant={only || 'combined'}
        >
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4" data-testid="reports-count-header">
                <div>
                    <h3 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent flex items-center gap-3" data-testid="reports-count-title">
                        {pageTitle}
                    </h3>
                    {pageSubtitle && (
                        <p className="mt-1 text-xs text-gray-400" data-testid="reports-count-subtitle">
                            {pageSubtitle}
                        </p>
                    )}
                </div>

                <div className="relative" data-testid="reports-count-download-container">
                    <motion.button
                        ref={downloadButtonRef}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setIsDownloadMenuOpen(!isDownloadMenuOpen)}
                        disabled={downloadLoading || showLoader || chartData.length === 0}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] rounded-lg shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                        data-testid="reports-count-download-button"
                    >
                        <Download className="w-4 h-4" data-testid="reports-count-download-icon" />
                        {t('reportsCount.download')}
                    </motion.button>

                    {/* Download Options Popup */}
                    {isDownloadMenuOpen && (
                        <motion.div
                            ref={downloadMenuRef}
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="absolute z-20 top-full right-0 mt-2 bg-white shadow-2xl rounded-xl border border-gray-200 w-48 overflow-hidden"
                            data-testid="reports-count-download-menu"
                        >
                            <button
                                onClick={handleDownloadExcel}
                                disabled={downloadLoading}
                                className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-cyan-50 transition-all duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid="reports-count-download-excel-button"
                            >
                                <FileSpreadsheet className="w-5 h-5 text-green-600" data-testid="reports-count-excel-icon" />
                                <span data-testid="reports-count-excel-text">{downloadLoading ? t('reportsCount.downloading') : t('reportsCount.excel')}</span>
                            </button>
                            <div className="border-t border-gray-100"></div>
                            <button
                                onClick={handleDownloadJPG}
                                className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-cyan-50 transition-all duration-200 cursor-pointer"
                                data-testid="reports-count-download-jpg-button"
                            >
                                <Image className="w-5 h-5 text-blue-600" data-testid="reports-count-jpg-icon" />
                                <span data-testid="reports-count-jpg-text">{t('reportsCount.jpg')}</span>
                            </button>
                        </motion.div>
                    )}
                </div>
            </div>

            {/* Tabs & Filter */}
            <div className="flex flex-col gap-4 mb-6" data-testid="reports-count-tabs-filter-section">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start gap-2">
                    {/* Tabs */}
                    <div className="grid grid-cols-2 sm:flex sm:flex-row gap-2 bg-gradient-to-r from-gray-50 to-blue-50/30 w-fit rounded-2xl p-1 shadow border border-gray-200 relative" data-testid="reports-count-tabs-container">
                        {['today', 'week', 'month', 'custom'].map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`relative px-3 py-2 text-xs sm:text-sm font-medium rounded-xl transition-all duration-300 cursor-pointer z-10 ${activeTab === tab
                                    ? 'text-white'
                                    : 'text-gray-600 hover:text-[#4ABDBD]'
                                    }`}
                                data-testid={`reports-count-tab-${tab}`}
                            >
                                {activeTab === tab && (
                                    <motion.div
                                        layoutId="activeCountReportTab"
                                        className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] rounded-xl shadow-lg !m-0"
                                        transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                    />
                                )}
                                <span className="relative z-20" data-testid={`reports-count-tab-text-${tab}`}>
                                    {t(`reportsCount.${tab}`)}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* Filter Button */}
                    <div className="relative w-full sm:w-auto" data-testid="reports-count-filter-container">
                        <button
                            ref={filterButtonRef}
                            onClick={() => setIsFilterOpen(!isFilterOpen)}
                            className="group flex items-center justify-center gap-2 w-full sm:w-auto h-full px-3 py-2.5 text-white bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B] rounded-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer"
                            aria-label={t('reportsCount.filter')}
                            aria-expanded={isFilterOpen}
                            data-testid="reports-count-filter-button"
                        >
                            <SlidersHorizontal className="w-5 h-5" data-testid="reports-count-filter-icon" />
                            {/* {t('reportsCount.filter')} */}
                        </button>

                        {isFilterOpen && (
                            <FilterPopover ref={filterRef}>
                                <FilterPanel
                                    onClear={() => {
                                        setTempFilters({ gender: [], countryId: [] });
                                        setFilters({ gender: [], countryId: [] });
                                        setIsFilterOpen(false);
                                    }}
                                    onApply={() => {
                                        setFilters(tempFilters);
                                        setIsFilterOpen(false);
                                    }}
                                    clearLabel={t('reportsCount.filter_clear')}
                                    applyLabel={t('reportsCount.filter_apply')}
                                >
                                    <FilterSection title={t('reportsCount.filter_gender')} isActive={tempFilters.gender.length > 0}>
                                        <FilterOptionList
                                            multiple
                                            value={tempFilters.gender}
                                            onChange={(val) => setTempFilters({ ...tempFilters, gender: val })}
                                            options={[
                                                { value: 'male', label: t('reportsCount.filter_male') },
                                                { value: 'female', label: t('reportsCount.filter_female') },
                                                { value: 'Not_Communicated', label: t('reportsCount.filter_Not_Communicated') },
                                                { value: 'other', label: t('reportsCount.filter_others') },
                                            ]}
                                        />
                                    </FilterSection>

                                    <FilterSection title={t('reportsCount.filter_country')} isActive={tempFilters.countryId.length > 0}>
                                        <FilterCountryList
                                            multiple
                                            value={tempFilters.countryId}
                                            onChange={(val) => setTempFilters({ ...tempFilters, countryId: val })}
                                            countries={countries}
                                            searchPlaceholder={t('reportsCount.search_placeholder', 'Search...')}
                                        />
                                    </FilterSection>
                                </FilterPanel>
                            </FilterPopover>
                        )}
                    </div>
                </div>

                {activeTab === 'custom' && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="flex flex-col sm:flex-row gap-2"
                        data-testid="reports-count-custom-date-section"
                    >
                        <div className="w-full sm:flex-1" data-testid="reports-count-from-date-field-container">
                            <CustomDatePicker
                                value={startDate}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setStartDate(value);
                                    // Validate dates when start date changes
                                    if (value || endDate) {
                                        validateDates(value, endDate);
                                    } else {
                                        setDateErrors({ startDate: '', endDate: '' });
                                    }
                                }}
                                placeholder={t('reportsCount.from_date')}
                            />
                            {dateErrors.startDate && (
                                <p className="text-red-600 text-xs mt-1" data-testid="reports-count-from-date-error">{dateErrors.startDate}</p>
                            )}
                        </div>
                        <div className="w-full sm:flex-1" data-testid="reports-count-to-date-field-container">
                            <CustomDatePicker
                                value={endDate}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setEndDate(value);
                                    // Validate dates when end date changes
                                    if (value || startDate) {
                                        validateDates(startDate, value);
                                    } else {
                                        setDateErrors({ startDate: '', endDate: '' });
                                    }
                                }}
                                minDate={startDate}
                                maxDate={new Date().toISOString().split('T')[0]}
                                placeholder={t('reportsCount.to_date')}
                            />
                            {dateErrors.endDate && (
                                <p className="text-red-600 text-xs mt-1" data-testid="reports-count-to-date-error">{dateErrors.endDate}</p>
                            )}
                        </div>
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => {
                                setStartDate('');
                                setEndDate('');
                                setDateErrors({ startDate: '', endDate: '' });
                            }}
                            className="flex items-center justify-center gap-1.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-lg px-4 py-2 hover:bg-[#4ABDBD]/10 hover:text-[#4ABDBD] transition-all"
                            data-testid="reports-count-reset-button"
                        >
                            <RotateCcw className="w-4 h-4" data-testid="reports-count-reset-icon" />
                            {t('reportsCount.reset')}
                        </motion.button>
                    </motion.div>
                )}
            </div>

            {/* Applied Filters Display */}
            {(filters.gender.length > 0 || filters.countryId.length > 0) && (
                <div className="mb-6 bg-gradient-to-r from-blue-50/80 to-cyan-50/80 rounded-xl p-4 border border-gray-200 shadow-sm" data-testid="reports-count-applied-filters-container">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-gray-700 mr-2" data-testid="reports-count-applied-filters-label">
                            {t('reportsCount.applied_filters')}:
                        </span>

                        {filters.gender.map((g) => (
                            <div key={`gender-${g}`} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm" data-testid={`reports-count-applied-gender-chip-${g}`}>
                                <span className="text-xs text-gray-600">
                                    <span className="font-semibold">{t('reportsCount.filter_gender')}:</span> {t(`reportsCount.filter_${g}`)}
                                </span>
                                <button
                                    onClick={() => setFilters({ ...filters, gender: filters.gender.filter((x) => x !== g) })}
                                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                    data-testid={`reports-count-applied-gender-remove-button-${g}`}
                                >
                                    <X className="h-3.5 w-3.5" data-testid={`reports-count-applied-gender-remove-icon-${g}`} />
                                </button>
                            </div>
                        ))}

                        {filters.countryId.map((cid) => (
                            <div key={`country-${cid}`} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm" data-testid={`reports-count-applied-country-chip-${cid}`}>
                                <span className="text-xs text-gray-600">
                                    <span className="font-semibold">{t('reportsCount.filter_country')}:</span> <MasterLabel type="masterCountry" id={cid} field="countryName" fallback={countries?.find(c => c.countryId === cid)?.countryName || cid} />
                                </span>
                                <button
                                    onClick={() => setFilters({ ...filters, countryId: filters.countryId.filter((x) => x !== cid) })}
                                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                    data-testid={`reports-count-applied-country-remove-button-${cid}`}
                                >
                                    <X className="h-3.5 w-3.5" data-testid={`reports-count-applied-country-remove-icon-${cid}`} />
                                </button>
                            </div>
                        ))}

                        <button
                            onClick={() => {
                                setFilters({ gender: [], countryId: [] });
                                setTempFilters({ gender: [], countryId: [] });
                            }}
                            className="ml-auto inline-flex items-center gap-1.5 bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md cursor-pointer"
                            data-testid="reports-count-clear-all-button"
                        >
                            <X className="h-3.5 w-3.5" data-testid="reports-count-clear-all-icon" />
                            {t('reportsCount.clear_all')}
                        </button>
                    </div>
                </div>
            )}

            {/* Loading State */}
            {showLoader && (
                <div className="flex flex-col items-center justify-center py-4 pt-12" data-testid="reports-count-loading-container">
                    <CommonLoader />
                    <span className="text-xl text-[#4ABDBD]" data-testid="reports-count-loading-text">{t('reportsCount.loading')}</span>
                </div>
            )}

            {/* Enhanced Bar Chart */}
            {!showLoader && (
                <div ref={chartRef} className="bg-white p-4 rounded-lg" data-testid="reports-count-chart-container">
                    {/* Period total — single-series pages only. Lives inside
                        the chart wrapper so the JPG export carries it too. */}
                    {singleSeries && chartData.length > 0 && (
                        <div className="mb-3 flex flex-wrap items-baseline gap-2" data-testid="reports-count-total">
                            <span className="text-sm font-semibold text-gray-700">
                                {doctorsOnly ? t('reportsCount.total_doctors', 'Total doctors') : t('reportsCount.total_patients', 'Total patients')}:
                            </span>
                            <span className="text-2xl font-bold" style={{ color: doctorsOnly ? '#4ABDBD' : '#F59E0B' }} data-testid="reports-count-total-value">
                                {seriesTotal}
                            </span>
                            <span className="text-xs text-gray-400">{t('reportsCount.in_range', 'in the selected period')}</span>
                        </div>
                    )}
                    {chartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={380}>
                            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="4 4" stroke="#e0e0e0" opacity={0.5} />
                                <XAxis
                                    dataKey="date"
                                    tick={{ fontSize: 13, fill: '#666' }}
                                    // angle={-45}
                                    textAnchor="end"
                                    height={70}
                                />
                                <YAxis tick={{ fontSize: 13, fill: '#666' }} />
                                <Tooltip
                                    cursor={{ fill: 'rgba(74, 189, 189, 0.1)' }}
                                    contentStyle={{
                                        backgroundColor: 'white',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '12px',
                                        boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                                        padding: '12px',
                                        fontSize: '13px'
                                    }}
                                    labelStyle={{ fontWeight: 'bold', color: '#4ABDBD', marginBottom: '8px' }}
                                    formatter={(value) => <span className="font-semibold">{value}</span>}
                                />
                                {/* Two series → legend doubles as the show/hide
                                    toggle. One series → no legend and no toggle:
                                    a switch that can only blank the chart is not
                                    a control. */}
                                {!singleSeries && (
                                    <Legend
                                        verticalAlign="top"
                                        height={50}
                                        iconType="circle"
                                        wrapperStyle={{ paddingBottom: '10px' }}
                                        onClick={(e) => handleLegendClick(e.dataKey)}
                                        formatter={(value, entry) => {
                                            const isVisible = visibleBars[entry.dataKey];
                                            return (
                                                <span
                                                    className={`text-sm font-medium capitalize cursor-pointer select-none transition-all ${isVisible ? 'text-gray-700' : 'text-gray-400 line-through'
                                                        }`}
                                                    style={{
                                                        opacity: isVisible ? 1 : 0.5,
                                                        textDecoration: isVisible ? 'none' : 'line-through'
                                                    }}
                                                >
                                                    {value}
                                                </span>
                                            );
                                        }}
                                    />
                                )}
                                {!patientsOnly && (
                                    <Bar
                                        dataKey="doctor"
                                        fill="#4ABDBD"
                                        name={t('reportsCount.doctors')}
                                        radius={[12, 12, 0, 0]}
                                        barSize={20}
                                        hide={!singleSeries && !visibleBars.doctor}
                                    />
                                )}
                                {!doctorsOnly && (
                                    <Bar
                                        dataKey="patient"
                                        fill="#F59E0B"
                                        name={t('reportsCount.patients')}
                                        radius={[12, 12, 0, 0]}
                                        barSize={20}
                                        hide={!singleSeries && !visibleBars.patient}
                                    />
                                )}
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-96 text-gray-400" data-testid="reports-count-no-data-container">
                            <Calendar className="w-16 h-16 mb-4 text-gray-300" data-testid="reports-count-no-data-icon" />
                            <p className="text-lg font-medium text-gray-500" data-testid="reports-count-no-data-text">{t('reportsCount.no_data')}</p>
                            <p className="text-sm text-gray-400 mt-1" data-testid="reports-count-no-data-subtitle">{t('reportsCount.no_data_subtitle')}</p>
                        </div>
                    )}
                </div>
            )}
        </motion.div>
    );
};

export default ReportsCount;